import React, { useRef, useState } from "react";
import api, { API, getToken } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Mic, Square, Loader2, Sparkles, Trophy } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";

function useRecorder() {
  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      rec.start();
      recRef.current = rec;
      setSeconds(0);
      setState("recording");
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch (e) {
      logger.error("Mic access denied", e);
      toast.error("Microphone permission needed");
    }
  };

  const stop = () =>
    new Promise((resolve) => {
      const rec = recRef.current;
      if (!rec) return resolve(null);
      setState("stopping");
      clearInterval(timerRef.current);
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        rec.stream.getTracks().forEach((t) => t.stop());
        recRef.current = null;
        setState("idle");
        resolve(blob);
      };
      rec.stop();
    });

  return { state, seconds, start, stop };
}

/**
 * Modal asks "What's the impact you created today?" before clock-out.
 * On submit: shows AI affirmation + auto-playing TTS audio, then calls
 * onComplete() which the parent uses to actually post the clock_out event.
 *
 * Props:
 *  - open / onClose: controlled visibility
 *  - onComplete: () => Promise<void>  // called AFTER user closes the affirmation
 */
export default function ImpactCheckoutModal({ open, onClose, onComplete }) {
  const [step, setStep] = useState("ask"); // ask | affirm
  const [impact, setImpact] = useState("");
  const [busy, setBusy] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [record, setRecord] = useState(null);
  const audioRef = useRef(null);
  const rec = useRecorder();

  const reset = () => {
    setStep("ask");
    setImpact("");
    setRecord(null);
  };

  const transcribe = async () => {
    if (rec.state !== "recording") return;
    const blob = await rec.stop();
    if (!blob) return;
    setTranscribing(true);
    try {
      const form = new FormData();
      form.append("file", blob, "impact.webm");
      const token = getToken();
      const resp = await fetch(`${API}/attendance/impact/transcribe`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || "Transcription failed");
      }
      const { text } = await resp.json();
      setImpact((c) => (c ? `${c}\n${text}` : text));
      toast.success("Got your voice — review before submitting");
    } catch (e) {
      toast.error(e.message || "Could not transcribe");
    } finally {
      setTranscribing(false);
    }
  };

  const submit = async () => {
    if (impact.trim().length < 5) {
      toast.error("Tell us a little more — anything counts");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/attendance/impact", { impact: impact.trim() });
      setRecord(data);
      setStep("affirm");
      if (data.affirmation_audio_b64 && audioRef.current) {
        audioRef.current.src = `data:audio/mp3;base64,${data.affirmation_audio_b64}`;
        try { await audioRef.current.play(); } catch (e) { logger.debug("autoplay blocked; user can click Play", e); }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to submit");
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    try {
      await onComplete?.();
    } finally {
      reset();
      onClose?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && step === "ask") onClose?.(); }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden border-0"
        data-testid="impact-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideClose
      >
        {step === "ask" && (
          <div className="p-8 space-y-4 bg-gradient-to-b from-indigo-50 to-white" data-testid="impact-step-ask">
            <div className="flex items-center gap-2">
              <Trophy className="w-7 h-7 text-indigo-500" strokeWidth={1.5} />
              <h2 className="font-display text-2xl font-black tracking-tight">What&apos;s the impact you created today?</h2>
            </div>
            <p className="text-sm text-zinc-600">
              Big or small — the room you turned around, the guest you helped, the teammate you supported. Tell us briefly so management knows.
            </p>
            <Textarea
              rows={5}
              value={impact}
              onChange={(e) => setImpact(e.target.value)}
              placeholder="e.g. Finished all rooms before 11, helped onboard the new joiner, fixed the AC in 204…"
              data-testid="impact-text"
            />
            <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
              {rec.state === "recording" ? (
                <Button onClick={transcribe} variant="outline" className="border-red-300 text-red-700" data-testid="impact-stop-record">
                  <Square className="w-4 h-4 mr-2 fill-red-700" /> Stop &amp; transcribe ({rec.seconds}s)
                </Button>
              ) : transcribing ? (
                <Button disabled variant="outline">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Transcribing…
                </Button>
              ) : (
                <Button onClick={rec.start} variant="outline" data-testid="impact-record">
                  <Mic className="w-4 h-4 mr-2" /> Record voice
                </Button>
              )}
              <Button
                onClick={submit}
                disabled={busy || impact.trim().length < 5}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="impact-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Share &amp; clock out
              </Button>
            </div>
          </div>
        )}

        {step === "affirm" && record && (
          <div className="p-8 space-y-4 bg-gradient-to-b from-emerald-50 to-white" data-testid="impact-step-affirm">
            <div className="text-center">
              <Sparkles className="w-7 h-7 mx-auto text-emerald-500" strokeWidth={1.5} />
              <h2 className="font-display text-2xl font-black tracking-tight mt-2">A note from management</h2>
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl p-5 text-zinc-800 text-[15px] leading-relaxed whitespace-pre-wrap" data-testid="impact-affirmation-text">
              {record.affirmation_text}
            </div>
            {record.affirmation_audio_b64 && (
              <div className="flex items-center justify-center">
                <audio ref={audioRef} controls className="w-full max-w-md" data-testid="impact-affirmation-audio">
                  <track kind="captions" />
                </audio>
              </div>
            )}
            <div className="flex justify-end pt-2">
              <Button onClick={finish} className="bg-zinc-950 hover:bg-zinc-800" data-testid="impact-finish">
                Sign off for today
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
